/**
 * CustomerProfileManagementPage — `/mail/customer-profile`.
 *
 * Staff-side customer lookup + walk-in registration. Distinct from the
 * citizen-facing `/portal/profile` page (which manages their OWN profile) —
 * here a counter clerk searches for any customer by phone / email / name OR
 * registers a brand-new walk-in customer at the counter before booking mail.
 *
 * Backend wiring (live):
 *   - GET  /v1/customers/lookup?phone=...|email=...|fullName=...
 *   - GET  /v1/customers/:id
 *   - POST /v1/customers  (walk-in registration — defaults grade=WALK_IN)
 */
import { type FormEvent, type JSX, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Search, User, UserPlus, X } from 'lucide-react';

import { ApiError } from '../../lib/api/client';
import { createCustomer, lookupCustomers } from '../../lib/api/customers';
import type { CreateCustomerRequest, CustomerResponse } from '../../lib/api/types';
import { DeferredNotice, PageHeader } from '../shared/StaffUI';

export function CustomerProfileManagementPage(): JSX.Element {
  const qc = useQueryClient();
  const [phone, setPhone] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [fullName, setFullName] = useState<string>('');
  const [submitted, setSubmitted] = useState<{ phone?: string; email?: string; fullName?: string } | null>(null);
  const [showRegister, setShowRegister] = useState<boolean>(false);
  const [justRegistered, setJustRegistered] = useState<CustomerResponse | null>(null);

  const lookup = useQuery({
    queryKey: ['mail', 'customer', submitted],
    queryFn: () => lookupCustomers({ ...submitted, pageSize: 25 }),
    enabled: submitted !== null,
    retry: false,
  });

  function handleSearch(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    const q: { phone?: string; email?: string; fullName?: string } = {};
    if (phone.trim()) q.phone = phone.trim();
    if (email.trim()) q.email = email.trim();
    if (fullName.trim()) q.fullName = fullName.trim();
    if (Object.keys(q).length === 0) return;
    setSubmitted(q);
  }

  const denied = lookup.isError && lookup.error instanceof ApiError && (lookup.error.status === 401 || lookup.error.status === 403);

  return (
    <div className="space-y-5">
      <PageHeader
        title="Customer Profile Management"
        description="Look up an existing customer, view their addresses, escalate to KYC — or register a walk-in customer at the counter."
        actions={
          <button
            type="button"
            onClick={() => setShowRegister(true)}
            data-testid="register-walk-in-btn"
            className="inline-flex items-center gap-2 px-4 py-2 bg-nipost-green text-white text-sm font-semibold rounded-lg hover:bg-nipost-green-dark"
          >
            <UserPlus className="w-4 h-4" /> Register walk-in
          </button>
        }
      />

      {justRegistered ? (
        <div
          data-testid="register-success"
          className="rounded-lg bg-emerald-50 border border-emerald-200 p-4 flex items-start justify-between gap-3"
        >
          <div className="text-sm text-emerald-900">
            <p className="font-semibold">Walk-in registered ✓</p>
            <p className="text-xs mt-1">
              <span className="font-mono">{justRegistered.customerNumber}</span> · {justRegistered.fullName} ·{' '}
              {justRegistered.primaryPhone}
            </p>
            <p className="text-xs text-emerald-700 mt-2">
              You can now book mail or top up the wallet for this customer at the counter.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setJustRegistered(null)}
            aria-label="Dismiss"
            className="text-emerald-700 hover:text-emerald-900"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      ) : null}

      <form onSubmit={handleSearch} className="bg-white border border-border rounded-xl p-5 space-y-3">
        <p className="text-xs text-muted">Provide at least one search field.</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <input
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="Phone (e.g. +234801…)"
            aria-label="Phone"
            className="px-3 py-2 text-sm border border-border rounded-lg"
          />
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email"
            aria-label="Email"
            className="px-3 py-2 text-sm border border-border rounded-lg"
          />
          <input
            type="text"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            placeholder="Full name"
            aria-label="Full name"
            className="px-3 py-2 text-sm border border-border rounded-lg"
          />
        </div>
        <div className="flex justify-end">
          <button
            type="submit"
            className="inline-flex items-center gap-2 px-4 py-2 bg-nipost-green text-white text-sm font-semibold rounded-lg hover:bg-nipost-green-dark"
          >
            <Search className="w-4 h-4" /> Search
          </button>
        </div>
      </form>

      {submitted === null ? (
        <p className="text-sm text-muted">Search to begin — or use "Register walk-in" to create a new customer.</p>
      ) : lookup.isPending ? (
        <p className="text-sm text-muted">Looking up…</p>
      ) : denied ? (
        <DeferredNotice title="Permission required">
          Token lacks <code className="font-mono">Customer/lookup</code>.
        </DeferredNotice>
      ) : lookup.isError ? (
        <DeferredNotice title="Service unreachable">
          {(lookup.error as Error).message}
        </DeferredNotice>
      ) : (lookup.data?.items.length ?? 0) === 0 ? (
        <div className="bg-white border border-border rounded-xl p-6 text-center space-y-3">
          <p className="text-sm text-muted">No customers matched that query.</p>
          <button
            type="button"
            onClick={() => setShowRegister(true)}
            className="inline-flex items-center gap-2 px-3 py-2 bg-nipost-green text-white text-sm font-semibold rounded-lg hover:bg-nipost-green-dark"
          >
            <UserPlus className="w-4 h-4" /> Register this customer as walk-in
          </button>
        </div>
      ) : (
        <div className="bg-white border border-border rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-muted">
              <tr>
                <th className="text-left px-4 py-2"><User className="inline w-3 h-3" /> Name</th>
                <th className="text-left px-4 py-2">Customer #</th>
                <th className="text-left px-4 py-2">Phone</th>
                <th className="text-left px-4 py-2">Email</th>
                <th className="text-left px-4 py-2">Grade</th>
                <th className="text-left px-4 py-2">KYC</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {lookup.data?.items.map((c) => (
                <tr key={c.id} className="hover:bg-gray-50">
                  <td className="px-4 py-2 font-semibold">{c.fullName}</td>
                  <td className="px-4 py-2 text-xs font-mono">{c.customerNumber}</td>
                  <td className="px-4 py-2 text-xs">{c.primaryPhone}</td>
                  <td className="px-4 py-2 text-xs">{c.primaryEmail ?? '—'}</td>
                  <td className="px-4 py-2 text-xs">
                    <span
                      className={`inline-block px-2 py-0.5 rounded text-[10px] font-semibold ${
                        c.grade === 'WALK_IN'
                          ? 'bg-amber-50 text-amber-800 border border-amber-200'
                          : 'bg-blue-50 text-blue-800 border border-blue-200'
                      }`}
                    >
                      {c.grade}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-xs">{c.kycLevel}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showRegister ? (
        <RegisterWalkInModal
          initial={{ phone, email, fullName }}
          onCancel={() => setShowRegister(false)}
          onSuccess={(customer) => {
            setShowRegister(false);
            setJustRegistered(customer);
            void qc.invalidateQueries({ queryKey: ['mail', 'customer'] });
          }}
        />
      ) : null}
    </div>
  );
}

interface RegisterWalkInModalProps {
  initial: { phone: string; email: string; fullName: string };
  onCancel: () => void;
  onSuccess: (customer: CustomerResponse) => void;
}

function RegisterWalkInModal({ initial, onCancel, onSuccess }: RegisterWalkInModalProps): JSX.Element {
  const [fullName, setFullName] = useState<string>(initial.fullName.trim());
  const [primaryPhone, setPrimaryPhone] = useState<string>(initial.phone.trim());
  const [primaryEmail, setPrimaryEmail] = useState<string>(initial.email.trim());
  const [error, setError] = useState<string | null>(null);

  const phoneValid = /^\+?[0-9]{10,15}$/.test(primaryPhone.replace(/\s/g, ''));
  const nameValid = fullName.trim().length >= 2;
  const emailValid = primaryEmail.trim().length === 0 || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(primaryEmail);
  const canSubmit = phoneValid && nameValid && emailValid;

  const createMut = useMutation({
    mutationFn: (dto: CreateCustomerRequest) => createCustomer(dto),
    onSuccess: (customer) => {
      onSuccess(customer);
    },
    onError: (e: Error) => {
      // ApiError exposes the parsed problem-details body on .detail
      if (e instanceof ApiError) {
        if (e.status === 401 || e.status === 403) {
          setError('You do not have permission to register a walk-in customer. Required: Customer/create.');
          return;
        }
        if (e.status === 409) {
          setError('A customer with this phone or email already exists. Use the search above to find them.');
          return;
        }
        setError(`${e.message} (HTTP ${e.status})`);
        return;
      }
      setError(e.message);
    },
  });

  function handleSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    if (!canSubmit) return;
    setError(null);
    // Normalise phone — strip spaces. Backend expects E.164 (+234…).
    let phone = primaryPhone.replace(/\s/g, '');
    if (!phone.startsWith('+') && phone.length === 11 && phone.startsWith('0')) {
      phone = `+234${phone.slice(1)}`;
    } else if (!phone.startsWith('+') && phone.length === 10) {
      phone = `+234${phone}`;
    }
    const dto: CreateCustomerRequest = {
      fullName: fullName.trim(),
      primaryPhone: phone,
      grade: 'WALK_IN',
      kycLevel: 'NONE',
    };
    if (primaryEmail.trim().length > 0) {
      dto.primaryEmail = primaryEmail.trim();
    }
    createMut.mutate(dto);
  }

  return (
    <div
      role="dialog"
      aria-label="Register walk-in customer"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full p-6 space-y-4">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-base font-semibold text-gray-900">Register walk-in customer</h3>
            <p className="text-xs text-muted mt-1">
              Captures the minimum needed to book mail or top up a wallet. Address + NIN can be added later.
            </p>
          </div>
          <button
            type="button"
            onClick={onCancel}
            aria-label="Close"
            className="text-gray-400 hover:text-gray-700"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label htmlFor="walkin-name" className="block text-xs font-medium text-gray-700 mb-1">
              Full name <span className="text-red-500">*</span>
            </label>
            <input
              id="walkin-name"
              data-testid="walkin-name"
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              required
              autoFocus
              className="w-full px-3 py-2 text-sm border border-border rounded-lg"
            />
          </div>
          <div>
            <label htmlFor="walkin-phone" className="block text-xs font-medium text-gray-700 mb-1">
              Phone <span className="text-red-500">*</span>
            </label>
            <input
              id="walkin-phone"
              data-testid="walkin-phone"
              type="tel"
              value={primaryPhone}
              onChange={(e) => setPrimaryPhone(e.target.value)}
              placeholder="+2348012345678 or 08012345678"
              required
              className="w-full px-3 py-2 text-sm border border-border rounded-lg font-mono"
            />
            {primaryPhone && !phoneValid ? (
              <p className="text-[10px] text-red-600 mt-1">Enter a valid Nigerian phone number.</p>
            ) : null}
          </div>
          <div>
            <label htmlFor="walkin-email" className="block text-xs font-medium text-gray-700 mb-1">
              Email (optional)
            </label>
            <input
              id="walkin-email"
              data-testid="walkin-email"
              type="email"
              value={primaryEmail}
              onChange={(e) => setPrimaryEmail(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-border rounded-lg"
            />
          </div>

          {error ? (
            <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-xs text-red-800">
              {error}
            </div>
          ) : null}

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 text-sm font-medium text-gray-700 border border-border rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!canSubmit || createMut.isPending}
              data-testid="walkin-submit"
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-nipost-green rounded-lg hover:bg-nipost-green-dark disabled:opacity-60"
            >
              <UserPlus className="w-4 h-4" />
              {createMut.isPending ? 'Registering…' : 'Register walk-in'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
